/**
 * The computer's live desktop link for an open sheet or the Computer page
 * (slice 6c §6): fetched when it opens, again on Watch again or Try again,
 * and lost (`taken`) when someone else opens the screen after it. And what
 * holding it takes: the hold kept every 5 minutes (§5), and the layout kept.
 */
import { useEffect, useEffectEvent, useRef, useState } from "react";
import { ApiError, errorHint } from "../../app/api";
import { useWide } from "../../app/wide";
import { controlLink, keepHold, useViewer, watchLink, type DesktopLink } from "./api";

export type Link =
  | { state: "loading" }
  | { state: "ready"; link: DesktopLink }
  | { state: "taken"; name: string }
  | { state: "failed"; text: string };

/**
 * A fresh link each time the sheet opens (or Watch again); dropped when it
 * closes, never cached. Boat's stream has one viewer: when someone opens the
 * screen after this sheet did, it's `taken`, rather than a frozen picture.
 * `get` fetches a link; `round` counts the agains (0 for the first link).
 */
function useDesktopLink(
  open: boolean,
  get: (signal: AbortSignal, round: number) => Promise<DesktopLink>,
  failText: (error: unknown) => string = errorHint,
): { link: Link; again: () => void } {
  const [link, setLink] = useState<Link>({ state: "loading" });
  const [round, setRound] = useState(0);
  const viewer = useViewer();
  // The latest `get` and `failText`, so a parent's re-render doesn't ask again.
  const fetchLink = useEffectEvent(get);
  const describe = useEffectEvent(failText);
  useEffect(() => {
    if (!open) return;
    const abort = new AbortController();
    const fetchInto = (signal: AbortSignal) =>
      fetchLink(signal, round).then(
        (l) => {
          if (signal.aborted) return;
          if (l?.url) setLink({ state: "ready", link: l });
          else setLink({ state: "failed", text: describe(null) });
        },
        (error: unknown) => {
          if (signal.aborted) return;
          setLink({ state: "failed", text: describe(error) });
        },
      );
    // Asked on the next tick, so a mount undone at once (React's StrictMode,
    // a page opened already open) asks for no link: one asked and dropped
    // would still be given out, and could take the stream from this one.
    const ask = setTimeout(() => void fetchInto(abort.signal));
    return () => {
      clearTimeout(ask);
      abort.abort();
      setLink({ state: "loading" });
    };
  }, [open, round]);
  // A link given out after this one took the stream (one viewer): by when
  // each was given, whatever order the answer and the event came in (Codex on #373).
  const lost =
    link.state === "ready" &&
    viewer !== null &&
    viewer.link_id !== link.link.id &&
    viewer.issued_at > link.link.issued_at;
  // Once taken it stays taken until Watch again: its link may no longer
  // reconnect, whatever the viewer reads later (Codex on #373).
  if (lost) setLink({ state: "taken", name: viewer.name });
  return {
    link: lost ? { state: "taken", name: viewer.name } : link,
    again: () => setRound((r) => r + 1),
  };
}

const watchFailText = (error: unknown) =>
  error instanceof ApiError && error.code === "asleep"
    ? "The computer is asleep. Watching doesn't wake it."
    : errorHint(error);

const getWatchLink = (signal: AbortSignal) => watchLink(signal);

/** A watch link while `open`: a fresh one each time, and again on Watch again. */
export const useWatchLink = (open: boolean) => useDesktopLink(open, getWatchLink, watchFailText);

/** §5: the sheet keeps its hold this often; a hold lasts 15 minutes. */
const KEEP_EVERY_MS = 5 * 60_000;

/** What a take answered: the control link, or why Boat gave none. */
export type FirstLink = DesktopLink | { error: string };

export const SIGN_IN_NOTE =
  "A sign-in you make here stays on the crew's computer, and the crew can use it for anyone's ask.";

const failText = (error: unknown) =>
  error instanceof ApiError && error.code === "asleep"
    ? "The computer went to sleep. Ask the agent again to wake it."
    : error instanceof ApiError
      ? error.hint
      : "Couldn't open the computer's screen.";

/**
 * The take's own link first; a new control link on Try again or Take it back.
 * A take that came with no link shows why, with Try again.
 */
export function useControlLink(open: boolean, first: FirstLink | null) {
  // A link a retake already brought, for the next round (Codex on #374).
  const given = useRef<DesktopLink | null>(null);
  const link = useDesktopLink(
    open,
    (signal, round) => {
      const next = given.current;
      given.current = null;
      if (next) return Promise.resolve(next);
      if (round === 0 && first) {
        return "url" in first
          ? Promise.resolve(first)
          : Promise.reject(new Error("Couldn't open the computer's screen."));
      }
      return controlLink(signal);
    },
    failText,
  );
  /** Again, with the take's own link when it brought one, so none is wasted. */
  const againWith = (taken: FirstLink) => {
    given.current = "url" in taken ? taken : null;
    link.again();
  };
  return { ...link, againWith };
}

/**
 * Keeps the hold while the sheet or page is open (§5), and again when the tab
 * comes back. A hold that ran out (`409`) is `lost`: the crew's asks may be
 * running, so control stops until it's taken again (Codex on #374). Any other
 * failure is the next keep's to retry.
 */
export function useKeepHold(open: boolean) {
  const [lost, setLost] = useState(false);
  useEffect(() => {
    if (!open) return;
    const keep = () =>
      void keepHold().catch((e: unknown) => {
        if (e instanceof ApiError && e.code === "bad_transition") setLost(true);
      });
    const resume = () => {
      if (document.visibilityState === "visible") keep();
    };
    const timer = setInterval(keep, KEEP_EVERY_MS);
    document.addEventListener("visibilitychange", resume);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", resume);
    };
  }, [open]);
  return { lost, found: () => setLost(false) };
}

/**
 * The layout the sheet opened with, kept until it closes: turning a phone
 * sideways crosses `md`, and a new layout would mount a new iframe, which
 * past its link's 10 minutes can't reconnect (Codex on #373).
 */
export function useLayoutAtOpen(open: boolean): boolean {
  const wide = useWide();
  const [atOpen, setAtOpen] = useState<boolean | null>(null);
  if (open && atOpen === null) setAtOpen(wide);
  if (!open && atOpen !== null) setAtOpen(null);
  return atOpen ?? wide;
}
